import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { uniq } from 'lodash-es';
import { useParams } from 'react-router';

import { useWindowTitle } from '@hotjar/webapp/hooks/useWindowTitle';
import { validateEmail } from '@hotjar/webapp/lib/email';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import type { Organization, OrganizationAccess, Invitation } from '@hotjar/organization';
import { Button, useNotify, Alert } from '@hotjar/hotsauce';
import * as gettingStartedGuide from '@hotjar/webapp/domains/activation/getting-started-guide';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useOrganizations, invite } from '@hotjar/organization';
import { logError } from '@hotjar/insights-common/lib/logger';

import { S } from './Styles';
import { OrganizationSelector } from './OrganizationSelector';
import { InvitationForm } from './InvitationForm';
import type { InvitationWithRowId } from './InvitationForm';
import { Layout } from './Layout';

function logInviteError(
  error: Error | string,
  invitations: Invitation[],
  organization: Organization,
) {
  const emails = invitations.map((invitation) => invitation.email);

  logError(
    new Error(
      `Failed to invite users: "${emails.join(', ')}" in organization ${
        organization.id
      }. Error message: ${error instanceof Error ? error.message : error}`,
    ),
  );
}

const validateInput = (invitations: InvitationWithRowId[]) => {
  // check if the same email is entered twice
  const emails = invitations.map(({ email }) => email).filter((email) => email.length);

  if (uniq(emails).length < emails.length) {
    return 'invalid-input-email-uniq';
  }

  for (let i = 0; i < invitations.length; i += 1) {
    const { email, access } = invitations[i];
    if (email || access) {
      if (!validateEmail(email)) {
        return 'invalid-input-invalid-email';
      }

      if (!access) {
        return 'invalid-input';
      }
    }
  }

  return null;
};

const MAX_INVITES = 15;
const TRACK_INVITE_SENT = 'User invite sent';
const INITIAL_INVITATIONS = [
  { rowId: 1, email: '', access: null },
  { rowId: 2, email: '', access: null },
  { rowId: 3, email: '', access: null },
];

export const TeamInvitePage = (): JSX.Element | null => {
  const { t } = useTranslation();

  useWindowTitle(t('common:team-invite.page-title'));

  const { organizationId } = useParams();
  const { data: site } = useCurrentSite();
  const { data: user } = useCurrentUser();
  const { data: orgs } = useOrganizations(user?.id);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { showToast } = useNotify();
  const { formSubmit: trackFormSubmit } = useTrack();

  const adminOrgs = orgs?.filter((x) => x.access === 'admin');
  const hasOrgWithAdminAccess = Boolean(adminOrgs?.length);

  useEffect(() => {
    if (organizationId && adminOrgs) {
      const org = adminOrgs.find((x) => String(x.id) === organizationId);
      setSelectedOrg(org ?? null);
    }
  }, [adminOrgs, organizationId]);

  const [invitations, setInvitations] = useState<InvitationWithRowId[]>(INITIAL_INVITATIONS);

  let nextRowId = invitations.length;

  function maybeAddNewField() {
    const hasInvalidEmails = invitations.some((row) => !validateEmail(row.email));
    const canAdd = invitations.length < MAX_INVITES && !hasInvalidEmails;

    if (canAdd) {
      nextRowId += 1;
      setInvitations([...invitations, { rowId: nextRowId, email: '', access: null }]);
    }
  }

  useEffect(() => {
    maybeAddNewField();
  });

  const onEmailChange = (index: number, email: string) => {
    setInvitations(invitations.map((row, i) => (i === index ? { ...row, email } : row)));
  };

  const onAccessChange = (index: number, access: OrganizationAccess) => {
    setInvitations(invitations.map((row, i) => (i === index ? { ...row, access } : row)));
  };

  const sendInvites = () => {
    if (!selectedOrg || !site || !user) {
      return;
    }

    const validationError = validateInput(invitations);

    if (validationError) {
      showToast({
        kind: 'danger',
        text: t(`common:team-invite.${validationError}`),
      });
      return;
    }

    const toSend: Invitation[] = [];

    invitations.forEach((invitation) => {
      if (invitation.access) {
        toSend.push({ email: invitation.email, access: invitation.access });
      }
    });

    if (toSend.length === 0) {
      showToast({
        kind: 'danger',
        text: t('common:team-invite.invalid-input'),
      });
      return;
    }

    setIsProcessing(true);

    invite(selectedOrg.accountId, selectedOrg.id, toSend)
      .then((response) => {
        if (!response.success) {
          const error = response.error ? response.error : t('common:team-invite.invitation-error');
          return Promise.reject(error);
        }

        gettingStartedGuide.markInviteAsCompleted(site, user.id);

        trackFormSubmit(TRACK_INVITE_SENT, true, {}, {});

        showToast({
          text: t('common:team-invite.invitation-sent'),
          kind: 'success',
        });

        navigateTo('/team/list');

        return response;
      })
      .catch((error) => {
        logInviteError(error, toSend, selectedOrg);

        showToast({
          text: t('common:team-invite.invitation-error'),
          kind: 'danger',
        });

        trackFormSubmit(
          TRACK_INVITE_SENT,
          false,
          {},
          {
            error,
          },
        );
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  if (!hasOrgWithAdminAccess) {
    return (
      <Layout>
        <Alert kind="danger">{t('common:team-invite.no-access')}</Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <OrganizationSelector
        isDisabled={Boolean(organizationId)}
        aria-label="organization selector"
        value={selectedOrg?.id}
        orgs={adminOrgs ?? []}
        onChange={(event) => {
          const { value } = event.target as HTMLSelectElement;
          const org = (adminOrgs ?? []).find((x) => String(x.id) === value);
          setSelectedOrg(org ?? null);
        }}
      />

      {selectedOrg && (
        <>
          <InvitationForm
            invitations={invitations}
            organization={selectedOrg}
            onAccessChange={onAccessChange}
            onEmailChange={onEmailChange}
          />

          <S.Footer>
            <Button
              aria-label="send invites"
              isLoading={isProcessing}
              loadingText="Loading"
              onClick={() => sendInvites()}
            >
              {t('common:team-invite.action-invite')}
            </Button>
          </S.Footer>
        </>
      )}
    </Layout>
  );
};
